import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  DocumentArrowDownIcon,
  PlusIcon,
  XMarkIcon,
  ExclamationCircleIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { useAppData } from '../../contexts/AppDataContext';
import toast from 'react-hot-toast';

const SupplierContracts = ({ suppliers, currentSupplierId }) => {
  const { getPurchases } = useAppData();
  const [contracts, setContracts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showNewContractModal, setShowNewContractModal] = useState(false);
  const [showViewContractModal, setShowViewContractModal] = useState(false);
  const [showEditContractModal, setShowEditContractModal] = useState(false);
  const [selectedSupplierId, setSelectedSupplierId] = useState(currentSupplierId || '');
  const [selectedContract, setSelectedContract] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const [contractData, setContractData] = useState({
    title: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    value: '',
    status: 'active',
    document: '',
    fileData: null,
    fileType: '',
  });

  // Charger les contrats depuis localStorage
  useEffect(() => {
    try {
      setIsLoading(true);
      // Charger les contrats existants
      const storedContracts = JSON.parse(localStorage.getItem('stockpro_contracts')) || [];
      setContracts(storedContracts);
      
      // Générer des contrats à partir des commandes d'achat si nécessaire
      if (storedContracts.length === 0) {
        const purchases = getPurchases() || [];
        if (purchases.length > 0) {
          console.log('Génération de contrats à partir des commandes:', purchases.length);
          
          // Regrouper les achats par fournisseur
          const supplierPurchases = {};
          purchases.forEach(purchase => {
            if (purchase.supplierId || purchase.supplier) {
              const supplierId = purchase.supplierId || `SUP-${Math.random().toString(36).substr(2, 6)}`;
              const supplierName = purchase.supplier || 'Fournisseur';
              
              if (!supplierPurchases[supplierId]) {
                supplierPurchases[supplierId] = {
                  supplier: { id: supplierId, name: supplierName },
                  purchases: []
                };
              }
              
              supplierPurchases[supplierId].purchases.push(purchase);
            }
          });
          
          // Créer des contrats pour chaque fournisseur avec des achats récurrents
          const generatedContracts = [];
          Object.values(supplierPurchases).forEach((data, index) => {
            if (data.purchases.length >= 2) {
              const supplier = data.supplier;
              const today = new Date();
              const startDate = new Date();
              startDate.setMonth(startDate.getMonth() - 3);
              
              const endDate = new Date();
              endDate.setFullYear(endDate.getFullYear() + 1);
              
              const totalValue = data.purchases.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
              
              generatedContracts.push({
                id: `CTR-${index + 100}`,
                supplierId: supplier.id,
                supplierName: supplier.name,
                title: `Contrat ${supplier.name} ${today.getFullYear()}`,
                startDate: startDate.toISOString().split('T')[0],
                endDate: endDate.toISOString().split('T')[0],
                value: `${Math.round(totalValue).toLocaleString()} DA`,
                status: 'active',
                document: `contrat_${supplier.name.toLowerCase().replace(/\s/g, '_')}_${today.getFullYear()}.pdf`
              });
            }
          });
          
          if (generatedContracts.length > 0) {
            // Sauvegarder les contrats générés
            localStorage.setItem('stockpro_contracts', JSON.stringify(generatedContracts));
            setContracts(generatedContracts);
            console.log(`${generatedContracts.length} contrats générés à partir des données d'achat`);
          }
        }
      } else {
        console.log(`${storedContracts.length} contrats chargés depuis localStorage`);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des contrats:', error);
      setContracts([]);
    } finally {
      setIsLoading(false);
    }
    
    // Préremplir le fournisseur si on vient de la page détail
    if (currentSupplierId) {
      setSelectedSupplierId(currentSupplierId);
    }
  }, [getPurchases, currentSupplierId]);

  // Créer un nouveau contrat
  const handleCreateContract = () => {
    if (!selectedSupplierId || !contractData.title || !contractData.startDate || !contractData.value) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    const selectedSupplier = suppliers.find(s => s.id === selectedSupplierId);
    
    if (!selectedSupplier) {
      toast.error('Fournisseur non trouvé');
      return;
    }

    // Générer un ID unique
    const timestamp = new Date().getTime();
    const uniqueId = `CTR-${timestamp.toString().slice(-6)}-${String(contracts.length + 1).padStart(3, '0')}`;
    
    const newContract = {
      id: uniqueId,
      supplierId: selectedSupplierId,
      supplierName: selectedSupplier.name,
      title: contractData.title,
      startDate: contractData.startDate,
      endDate: contractData.endDate || null,
      value: contractData.value,
      status: contractData.status || 'active',
      document: contractData.document || `contrat_${selectedSupplier.name.toLowerCase().replace(/\s/g, '_')}_${new Date().getFullYear()}.pdf`,
      fileData: contractData.fileData || null,
      fileType: contractData.fileType || ''
    };
    
    const updatedContracts = [...contracts, newContract];
    setContracts(updatedContracts);
    localStorage.setItem('stockpro_contracts', JSON.stringify(updatedContracts));
    
    toast.success('Nouveau contrat créé');
    setShowNewContractModal(false);
    resetContractForm();
  };

  // Handler pour voir les détails d'un contrat
  const handleViewContract = (contract) => {
    setSelectedContract(contract);
    setShowViewContractModal(true);
  };

  // Handler pour éditer un contrat
  const handleEditContract = (contract) => {
    setSelectedContract(contract);
    setContractData({
      title: contract.title || '',
      startDate: contract.startDate || new Date().toISOString().split('T')[0],
      endDate: contract.endDate || '',
      value: contract.value || '',
      status: contract.status || 'active',
      document: contract.document || '',
      fileData: contract.fileData || null,
      fileType: contract.fileType || ''
    });
    setSelectedSupplierId(contract.supplierId);
    setShowEditContractModal(true);
  };

  // Handler pour mettre à jour un contrat existant
  const handleUpdateContract = () => {
    if (!selectedContract || !contractData.title || !contractData.startDate || !contractData.value) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    const updatedContracts = contracts.map(c => 
      c.id === selectedContract.id 
        ? { 
            ...selectedContract,
            title: contractData.title,
            startDate: contractData.startDate,
            endDate: contractData.endDate || null,
            value: contractData.value,
            status: contractData.status,
            document: contractData.document || selectedContract.document,
            fileData: contractData.fileData || selectedContract.fileData,
            fileType: contractData.fileType || selectedContract.fileType,
            lastUpdated: new Date().toISOString()
          } 
        : c
    );

    setContracts(updatedContracts);
    localStorage.setItem('stockpro_contracts', JSON.stringify(updatedContracts));
    
    toast.success('Contrat mis à jour avec succès');
    setShowEditContractModal(false);
    resetContractForm();
  };

  // Handler pour supprimer un contrat
  const handleDeleteContract = (contractId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce contrat ? Cette action est irréversible.')) {
      const updatedContracts = contracts.filter(c => c.id !== contractId);
      
      setContracts(updatedContracts);
      localStorage.setItem('stockpro_contracts', JSON.stringify(updatedContracts));
      
      toast.success('Contrat supprimé avec succès');
    }
  };

  // Gérer le téléchargement du document
  const handleDownloadDocument = (contract) => {
    if (!contract.fileData) {
      toast.error('Aucun document disponible pour ce contrat');
      return;
    }

    try {
      // Créer un lien pour télécharger le fichier
      const link = document.createElement('a');
      link.href = contract.fileData;
      link.download = contract.document || `contrat-${contract.id}.${contract.fileType.split('/')[1] || 'pdf'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Erreur lors du téléchargement du document:', error);
      toast.error('Erreur lors du téléchargement du document');
    }
  };

  // Gérer le glisser-déposer de fichiers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  // Gérer la sélection de fichier
  const handleFileSelect = (file) => {
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) { // 5MB max
      toast.error('Le fichier est trop volumineux. La taille maximale est de 5 Mo.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const fileName = file.name;
      setContractData({
        ...contractData,
        document: fileName,
        fileData: e.target.result,
        fileType: file.type
      });
      toast.success(`Fichier "${fileName}" ajouté`);
    };
    reader.readAsDataURL(file);
  };

  // Trigger le sélecteur de fichier
  const handleClickUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Réinitialiser le formulaire
  const resetContractForm = () => {
    setContractData({
      title: '',
      startDate: new Date().toISOString().split('T')[0],
      endDate: '',
      value: '',
      status: 'active',
      document: '',
      fileData: null,
      fileType: ''
    });
    setSelectedContract(null);
    setIsDragging(false);
  };

  // Obtenir la couleur du statut
  const getStatusColor = (status) => {
    switch(status) {
      case 'active':
        return 'bg-green-100/10 text-green-400';
      case 'expired':
        return 'bg-red-100/10 text-red-400';
      case 'pending':
        return 'bg-amber-100/10 text-amber-400';
      default:
        return 'bg-white/10 text-white/70';
    }
  };
  
  // Obtenir le texte du statut
  const getStatusText = (status) => {
    switch(status) {
      case 'active':
        return 'Actif';
      case 'expired':
        return 'Expiré';
      case 'pending':
        return 'En attente';
      default:
        return status;
    }
  };

  return (
    <div className="space-y-6">
      {/* Contrats actifs */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden">
        <div className="p-6 border-b border-white/10 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-white">Contrats actifs</h2>
          <button
            onClick={() => setShowNewContractModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl transition-colors"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Nouveau contrat</span>
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-white/10">
            <thead className="bg-white/5">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Fournisseur</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Contrat</th>
                <th className="px-6 py-4 text-center text-xs font-medium text-white/70 uppercase tracking-wider">Période</th>
                <th className="px-6 py-4 text-center text-xs font-medium text-white/70 uppercase tracking-wider">Valeur</th>
                <th className="px-6 py-4 text-center text-xs font-medium text-white/70 uppercase tracking-wider">Statut</th>
                <th className="px-6 py-4 text-center text-xs font-medium text-white/70 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {isLoading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                    </div>
                    <p className="mt-2 text-white/70">Chargement des contrats...</p>
                  </td>
                </tr>
              ) : contracts.filter(c => {
                  // Filtrer par statut actif
                  const isActive = c.status === 'active';
                  
                  // Si on est sur une page de fournisseur spécifique, filtrer par fournisseur
                  if (currentSupplierId) {
                    return isActive && c.supplierId === currentSupplierId;
                  }
                  return isActive;
                }).length > 0 ? (
                  contracts
                    .filter(c => {
                      // Filtrer par statut actif
                      const isActive = c.status === 'active';
                      
                      // Si on est sur une page de fournisseur spécifique, filtrer par fournisseur
                      if (currentSupplierId) {
                        return isActive && c.supplierId === currentSupplierId;
                      }
                      return isActive;
                    })
                    .map((contract, index) => (
                      <tr key={contract.id} className="hover:bg-white/5 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{contract.supplierName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{contract.title}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-white/80">
                          {contract.startDate} {contract.endDate ? `- ${contract.endDate}` : ''}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-white">{contract.value}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(contract.status)}`}>
                            {getStatusText(contract.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center space-x-3">
                            <button 
                              onClick={() => handleViewContract(contract)}
                              className="text-blue-400 hover:text-blue-300 transition-colors"
                              aria-label="Voir le contrat"
                            >
                              <EyeIcon className="h-5 w-5" />
                            </button>
                            <button 
                              onClick={() => handleEditContract(contract)}
                              className="text-indigo-400 hover:text-indigo-300 transition-colors"
                              aria-label="Modifier le contrat"
                            >
                              <PencilIcon className="h-5 w-5" />
                            </button>
                            {contract.fileData && (
                              <button 
                                onClick={() => handleDownloadDocument(contract)}
                                className="text-green-400 hover:text-green-300 transition-colors"
                                aria-label="Télécharger le document"
                              >
                                <DocumentArrowDownIcon className="h-5 w-5" />
                              </button>
                            )}
                            <button 
                              onClick={() => handleDeleteContract(contract.id)}
                              className="text-red-400 hover:text-red-300 transition-colors"
                              aria-label="Supprimer le contrat"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center">
                      <div className="flex flex-col items-center">
                        <ExclamationCircleIcon className="h-10 w-10 text-white/40 mb-2" />
                        <p className="text-white/60">Aucun contrat actif trouvé</p>
                      </div>
                    </td>
                  </tr>
                )}
            </tbody>
          </table>
        </div>
      </div>



      {/* Modal pour créer un nouveau contrat */}
      {showNewContractModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 overflow-y-auto p-4">
          <div className="bg-gray-900 rounded-2xl max-w-2xl w-full border border-white/20 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gray-900 py-2 px-6 border-b border-white/10 flex justify-between items-center">
              <h3 className="text-xl font-semibold text-white">Nouveau contrat</h3>
              <button 
                onClick={() => setShowNewContractModal(false)}
                className="text-white/60 hover:text-white"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {!currentSupplierId && (
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Fournisseur <span className="text-red-400">*</span>
                    </label>
                    <select
                      value={selectedSupplierId}
                      onChange={(e) => setSelectedSupplierId(e.target.value)}
                      className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white"
                    >
                      <option value="">Sélectionner un fournisseur</option>
                      {suppliers.map(supplier => (
                        <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Titre du contrat <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={contractData.title}
                    onChange={(e) => setContractData({...contractData, title: e.target.value})}
                    placeholder="Ex: Contrat fourniture papeterie 2023"
                    className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Date de début <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="date"
                      value={contractData.startDate}
                      onChange={(e) => setContractData({...contractData, startDate: e.target.value})}
                      className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Date de fin
                    </label>
                    <input
                      type="date"
                      value={contractData.endDate}
                      onChange={(e) => setContractData({...contractData, endDate: e.target.value})}
                      className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Valeur <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={contractData.value}
                      onChange={(e) => setContractData({...contractData, value: e.target.value})}
                      placeholder="Ex: 450 000 DA"
                      className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30"
                    />
                  </div>
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Statut
                    </label>
                    <select
                      value={contractData.status}
                      onChange={(e) => setContractData({...contractData, status: e.target.value})}
                      className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white"
                    >
                      <option value="active">Actif</option>
                      <option value="pending">En attente</option>
                      <option value="expired">Expiré</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Document
                  </label>
                  <div
                    className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                      isDragging
                        ? 'border-blue-400 bg-blue-400/10'
                        : contractData.fileData
                        ? 'border-green-400 bg-green-400/10'
                        : 'border-white/20 bg-white/5 hover:border-white/30 hover:bg-white/10'
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={handleClickUpload}
                  >
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      accept="application/pdf,image/jpeg,image/png,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={(e) => e.target.files?.length && handleFileSelect(e.target.files[0])}
                    />

                    {contractData.fileData ? (
                      <div className="flex flex-col items-center">
                        <DocumentArrowDownIcon className="h-10 w-10 text-green-400 mb-2" />
                        <p className="text-white font-medium">{contractData.document}</p>
                        <p className="text-white/60 text-sm mt-1">Cliquez pour changer de fichier</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <DocumentArrowDownIcon className="h-10 w-10 text-white/40 mb-2" />
                        <p className="text-white">Glissez-déposez votre fichier ici</p>
                        <p className="text-white/60 text-sm mt-1">ou cliquez pour sélectionner un fichier</p>
                        <p className="text-white/40 text-xs mt-3">Formats acceptés: PDF, JPEG, PNG, DOC, DOCX</p>
                        <p className="text-white/40 text-xs">5 Mo maximum</p>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="text-white/50 text-xs mt-2">
                  <span className="text-red-400">*</span> Champs obligatoires
                </div>
              </div>
            </div>
            <div className="sticky bottom-0 bg-gray-900 p-4 border-t border-white/10 flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3">
              <button
                onClick={() => setShowNewContractModal(false)}
                className="px-4 py-2.5 bg-white/5 hover:bg-white/10 text-white rounded-xl transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleCreateContract}
                className="px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl transition-colors"
              >
                Créer le contrat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal pour voir les détails d'un contrat */}
      {showViewContractModal && selectedContract && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 overflow-y-auto p-4">
          <div className="bg-gray-900 rounded-2xl max-w-2xl w-full border border-white/20 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gray-900 py-2 px-6 border-b border-white/10 flex justify-between items-center">
              <h3 className="text-xl font-semibold text-white">Détails du contrat</h3>
              <button 
                onClick={() => setShowViewContractModal(false)}
                className="text-white/60 hover:text-white"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-medium text-white mb-2">Informations générales</h4>
                  <div className="bg-white/5 rounded-xl p-4 space-y-4">
                    <div>
                      <p className="text-sm text-white/60">ID du contrat</p>
                      <p className="text-white">{selectedContract.id}</p>
                    </div>
                    <div>
                      <p className="text-sm text-white/60">Fournisseur</p>
                      <p className="text-white">{selectedContract.supplierName}</p>
                    </div>
                    <div>
                      <p className="text-sm text-white/60">Titre</p>
                      <p className="text-white">{selectedContract.title}</p>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-lg font-medium text-white mb-2">Détails du contrat</h4>
                    <div className="bg-white/5 rounded-xl p-4 space-y-4">
                      <div>
                        <p className="text-sm text-white/60">Période</p>
                        <p className="text-white">
                          {selectedContract.startDate} {selectedContract.endDate ? `- ${selectedContract.endDate}` : ''}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-white/60">Valeur</p>
                        <p className="text-white">{selectedContract.value}</p>
                      </div>
                      <div>
                        <p className="text-sm text-white/60">Statut</p>
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedContract.status)}`}>
                          {getStatusText(selectedContract.status)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-medium text-white mb-2">Document</h4>
                    <div className="bg-white/5 rounded-xl p-4 h-full flex flex-col justify-center items-center">
                      {selectedContract.fileData ? (
                        <>
                          <DocumentArrowDownIcon className="h-10 w-10 text-blue-400 mb-2" />
                          <p className="text-sm text-white">{selectedContract.document}</p>
                          <button 
                            onClick={() => handleDownloadDocument(selectedContract)} 
                            className="mt-2 px-3 py-1 bg-blue-600 hover:bg-blue-700 transition-colors text-sm text-white rounded-lg"
                          >
                            Télécharger le document
                          </button>
                        </>
                      ) : (
                        <p className="text-white/50">Aucun document associé</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="sticky bottom-0 bg-gray-900 p-4 border-t border-white/10 flex justify-end">
              <button
                onClick={() => {
                  setShowViewContractModal(false);
                  handleEditContract(selectedContract);
                }}
                className="px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl transition-colors"
              >
                Modifier ce contrat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal pour éditer un contrat */}
      {showEditContractModal && selectedContract && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 overflow-y-auto p-4">
          <div className="bg-gray-900 rounded-2xl max-w-2xl w-full border border-white/20 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gray-900 py-2 px-6 border-b border-white/10 flex justify-between items-center">
              <h3 className="text-xl font-semibold text-white">Modifier le contrat</h3>
              <button 
                onClick={() => setShowEditContractModal(false)}
                className="text-white/60 hover:text-white"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-white/70 mb-2">ID: <span className="text-white">{selectedContract.id}</span></p>
                </div>

                <div>
                  <p className="text-sm text-white/70 mb-2">Fournisseur: <span className="text-white">{selectedContract.supplierName}</span></p>
                </div>
                
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Titre du contrat <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={contractData.title}
                    onChange={(e) => setContractData({...contractData, title: e.target.value})}
                    className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Date de début <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="date"
                      value={contractData.startDate}
                      onChange={(e) => setContractData({...contractData, startDate: e.target.value})}
                      className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Date de fin
                    </label>
                    <input
                      type="date"
                      value={contractData.endDate}
                      onChange={(e) => setContractData({...contractData, endDate: e.target.value})}
                      className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Valeur <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={contractData.value}
                      onChange={(e) => setContractData({...contractData, value: e.target.value})}
                      className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Statut
                    </label>
                    <select
                      value={contractData.status}
                      onChange={(e) => setContractData({...contractData, status: e.target.value})}
                      className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white"
                    >
                      <option value="active">Actif</option>
                      <option value="pending">En attente</option>
                      <option value="expired">Expiré</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Document
                  </label>
                  <div
                    className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                      isDragging
                        ? 'border-blue-400 bg-blue-400/10'
                        : contractData.fileData || selectedContract.fileData
                        ? 'border-green-400 bg-green-400/10'
                        : 'border-white/20 bg-white/5 hover:border-white/30 hover:bg-white/10'
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={handleClickUpload}
                  >
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      accept="application/pdf,image/jpeg,image/png,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={(e) => e.target.files?.length && handleFileSelect(e.target.files[0])}
                    />

                    {contractData.fileData ? (
                      <div className="flex flex-col items-center">
                        <DocumentArrowDownIcon className="h-10 w-10 text-green-400 mb-2" />
                        <p className="text-white font-medium">{contractData.document}</p>
                        <p className="text-white/60 text-sm mt-1">Cliquez pour changer de fichier</p>
                      </div>
                    ) : selectedContract.fileData ? (
                      <div className="flex flex-col items-center">
                        <DocumentArrowDownIcon className="h-10 w-10 text-blue-400 mb-2" />
                        <p className="text-white font-medium">{selectedContract.document}</p>
                        <p className="text-white/60 text-sm mt-1">Document existant - Cliquez pour remplacer</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <DocumentArrowDownIcon className="h-10 w-10 text-white/40 mb-2" />
                        <p className="text-white">Glissez-déposez votre fichier ici</p>
                        <p className="text-white/60 text-sm mt-1">ou cliquez pour sélectionner un fichier</p>
                        <p className="text-white/40 text-xs mt-3">Formats acceptés: PDF, JPEG, PNG, DOC, DOCX</p>
                        <p className="text-white/40 text-xs">5 Mo maximum</p>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="text-white/50 text-xs mt-2">
                  <span className="text-red-400">*</span> Champs obligatoires
                </div>
              </div>
            </div>
            <div className="sticky bottom-0 bg-gray-900 p-4 border-t border-white/10 flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3">
              <button
                onClick={() => setShowEditContractModal(false)}
                className="px-4 py-2.5 bg-white/5 hover:bg-white/10 text-white rounded-xl transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleUpdateContract}
                className="px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl transition-colors"
              >
                Mettre à jour
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierContracts;